// AdminImprovementRequests — 4th tab on AdminDashboard.
//
// Admin review surface for proposals produced by the flaggedCallAnalyzer
// Cloud Function. Reads ONLY the flagged_call_proposals collection — never
// the source-flag collection. The consent commitment from the post-call flag
// modal is the architectural reason for that boundary; a static-grep check
// on this file is a regression gate (no reference to the source-flag
// collection name should appear here).

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, MenuItem, Select, Snackbar, Alert, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  collection, doc, onSnapshot, orderBy, query, serverTimestamp, updateDoc,
} from 'firebase/firestore';
import { db } from '../../FirebaseConfig';
import { useAuth } from '../auth/AuthContext';
import usePendingProposalCount from '../../hooks/usePendingProposalCount';
import './AdminImprovementRequests.css';

const TYPE_LABEL = {
  regression_test: 'Regression test',
  rules_yaml: 'Rules YAML',
  prompt_diff: 'Prompt diff',
  no_action: 'No action',
};
const TYPE_COLOR = {
  regression_test: 'info',
  rules_yaml: 'success',
  prompt_diff: 'warning',
  no_action: 'default',
};
const STATUS_COLOR = {
  pending: 'info',
  approved: 'success',
  rejected: 'error',
  hold: 'warning',
};

const formatTs = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString();
};

const PROPOSALS_COLLECTION = 'flagged_call_proposals';

const AdminImprovementRequests = () => {
  const { currentUser } = useAuth();
  const pendingCount = usePendingProposalCount();
  const [authzChecked, setAuthzChecked] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  // ── Authz check (custom claim isAdmin) ────────────────────────────
  useEffect(() => {
    if (!currentUser) { setAuthzChecked(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const tr = await currentUser.getIdTokenResult();
        if (!cancelled) setAuthorized(!!tr.claims.isAdmin);
      } catch (e) {
        if (!cancelled) setAuthorized(false);
      } finally {
        if (!cancelled) setAuthzChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser]);

  // ── Real-time list ────────────────────────────────────────────────
  useEffect(() => {
    if (!authorized) return undefined;
    setLoading(true);
    const q = query(
      collection(db, PROPOSALS_COLLECTION),
      orderBy('created_at', 'desc'),
    );
    const unsub = onSnapshot(q, (snap) => {
      setProposals(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => {
      setError('Failed to load proposals: ' + err.message);
      setLoading(false);
    });
    return unsub;
  }, [authorized]);

  // pending is sourced from usePendingProposalCount (single source of truth
  // shared with Sidebar + tab badge). approved/rejected/totalCost stay
  // window-filtered to the last 30 days because those are recent-activity
  // metrics, not the active review queue.
  const stats = useMemo(() => {
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    let approved = 0;
    let rejected = 0;
    let totalCost = 0;
    for (const p of proposals) {
      const created = p.created_at?.toDate?.()?.getTime?.() ?? 0;
      if (created < cutoff) continue;
      if (p.admin_decision === 'approved') approved++;
      else if (p.admin_decision === 'rejected') rejected++;
      totalCost += p.analyzer_token_usage?.cost_usd_estimate ?? 0;
    }
    return { approved, rejected, totalCost };
  }, [proposals]);

  const filtered = useMemo(() => proposals.filter((p) => (
    (statusFilter === 'all' || p.admin_decision === statusFilter)
      && (typeFilter === 'all' || p.proposal_type === typeFilter)
  )), [proposals, statusFilter, typeFilter]);

  const decide = async (decision) => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await updateDoc(doc(db, PROPOSALS_COLLECTION, selected.id), {
        admin_decision: decision,
        admin_decision_at: serverTimestamp(),
        admin_decision_by: currentUser.uid,
        admin_notes: adminNotes || '',
      });
      setToast({ severity: 'success', message: `Marked ${decision}.` });
      setTimeout(() => { setSelected(null); setAdminNotes(''); }, 600);
    } catch (e) {
      setToast({ severity: 'error', message: 'Update failed: ' + e.message });
    } finally {
      setSubmitting(false);
    }
  };

  if (!authzChecked) return <Box p={3}>Checking access…</Box>;
  if (!authorized) return <Box p={3}>Admin access required.</Box>;
  if (loading) return <Box p={3}>Loading proposals…</Box>;
  if (error) return <Box p={3}><Alert severity="error">{error}</Alert></Box>;

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="body2">
            <strong>{pendingCount}</strong> pending ·{' '}
            <strong>{stats.approved}</strong> approved ·{' '}
            <strong>{stats.rejected}</strong> rejected (last 30d)
            {' · '}
            Improvement Requests cost (last 30d):{' '}
            <strong>${stats.totalCost.toFixed(2)}</strong>
          </Typography>
        </CardContent>
      </Card>

      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          size="small"
          value={statusFilter}
          exclusive
          onChange={(_, v) => v && setStatusFilter(v)}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="pending">Pending</ToggleButton>
          <ToggleButton value="approved">Approved</ToggleButton>
          <ToggleButton value="rejected">Rejected</ToggleButton>
          <ToggleButton value="hold">Hold</ToggleButton>
        </ToggleButtonGroup>
        <Select
          size="small"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <MenuItem value="all">All types</MenuItem>
          {Object.entries(TYPE_LABEL).map(([k, v]) => (
            <MenuItem key={k} value={k}>{v}</MenuItem>
          ))}
        </Select>
      </Box>

      <TableContainer component={Card}>
        <Table size="small" className="admin-improvement-table">
          <TableHead>
            <TableRow>
              <TableCell>Created</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Failure pattern</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((p) => (
              <TableRow key={p.id}>
                <TableCell>{formatTs(p.created_at)}</TableCell>
                <TableCell>{p.source_contact_name}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={TYPE_LABEL[p.proposal_type] || p.proposal_type}
                    color={TYPE_COLOR[p.proposal_type] || 'default'}
                  />
                </TableCell>
                <TableCell sx={{ maxWidth: 400 }}>
                  <Tooltip title={p.failure_pattern || ''}>
                    <span style={{
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    >
                      {p.failure_pattern}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={p.admin_decision}
                    color={STATUS_COLOR[p.admin_decision] || 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    onClick={() => {
                      setSelected(p);
                      setAdminNotes(p.admin_notes || '');
                    }}
                  >
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <em>No proposals match these filters.</em>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={!!selected}
        onClose={() => setSelected(null)}
        maxWidth="md"
        fullWidth
      >
        {selected && (
          <>
            <DialogTitle sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
            >
              <span>
                {selected.source_contact_name} — {TYPE_LABEL[selected.proposal_type]}
              </span>
              <IconButton onClick={() => setSelected(null)}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Box mb={2}>
                <Chip
                  label={selected.admin_decision}
                  color={STATUS_COLOR[selected.admin_decision] || 'default'}
                />
                <Typography variant="caption" sx={{ ml: 1 }}>
                  Created {formatTs(selected.created_at)}
                  {selected.analyzer_context_version
                    && ` · ctx ${selected.analyzer_context_version}`}
                </Typography>
              </Box>

              <Typography variant="h6" sx={{ mb: 1 }}>Failure pattern</Typography>
              <Typography sx={{ mb: 2 }}>{selected.failure_pattern}</Typography>

              {selected.proposal_type === 'prompt_diff' && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  ⚠ Wide blast radius — manually run{' '}
                  <code>tests/run_tests.py --core</code> before deploying.
                </Alert>
              )}

              <Typography variant="h6" sx={{ mb: 1 }}>Rationale</Typography>
              <Typography sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                {selected.rationale}
              </Typography>

              <Typography variant="h6" sx={{ mb: 1 }}>Cited evidence</Typography>
              <Box className="cited-snippets" sx={{ mb: 2 }}>
                {(selected.cited_snippets || []).map((s, i) => (
                  // cited_snippets order is the LLM's evidence order; index is a
                  // stable enough key for a static list rendered inside one modal.
                  // eslint-disable-next-line react/no-array-index-key
                  <Box key={i} className={`snippet snippet-${s.role}`}>
                    <span className="turn-idx">turn {s.turn_index}</span>
                    <span className="role">{s.role}</span>
                    <span className="text">{s.text}</span>
                  </Box>
                ))}
              </Box>

              <Typography variant="h6" sx={{ mb: 1 }}>Proposal payload</Typography>
              <PayloadView
                proposal={selected}
                onCopiedClaudePrompt={() =>
                  setToast({ severity: 'success', message: 'Copied as Claude prompt.' })
                }
              />

              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Apply this patch</Typography>
              <ApplyInstructions proposal={selected} />

              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Decision</Typography>
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="Notes (optional)…"
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                sx={{ mb: 1 }}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              {selected.admin_decision !== 'pending' ? (
                <>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    Decided <strong>{selected.admin_decision}</strong> at{' '}
                    {formatTs(selected.admin_decision_at)}
                  </Typography>
                  <Button onClick={() => decide('pending')} disabled={submitting}>
                    Re-decide
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={() => decide('hold')}
                    color="warning"
                    disabled={submitting}
                  >
                    Hold
                  </Button>
                  <Button
                    onClick={() => decide('rejected')}
                    color="error"
                    disabled={submitting}
                  >
                    Reject
                  </Button>
                  <Button
                    onClick={() => decide('approved')}
                    color="success"
                    variant="contained"
                    disabled={submitting}
                  >
                    Approve
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
      >
        {toast && (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
};

// Builds the paste-ready block consumed by .claude/commands/apply-flagged-proposal.md
// when an admin clicks "Copy as Claude prompt" on an approved proposal.
const buildClaudePrompt = (proposal) => {
  const payload = {
    proposal_id: proposal.id,
    proposal_payload: proposal.proposal_payload,
    failure_pattern: proposal.failure_pattern,
    rationale: proposal.rationale,
  };
  return `/apply-flagged-proposal ${proposal.proposal_type}\n\n${JSON.stringify(payload, null, 2)}\n`;
};

const CopyAsClaudePromptButton = ({ proposal, onCopied }) => (
  <Button
    size="small"
    variant="outlined"
    onClick={() => {
      navigator.clipboard.writeText(buildClaudePrompt(proposal));
      if (onCopied) onCopied();
    }}
    sx={{ ml: 1 }}
  >
    Copy as Claude prompt
  </Button>
);

const PayloadView = ({ proposal, onCopiedClaudePrompt }) => {
  const p = proposal.proposal_payload;
  const t = proposal.proposal_type;
  if (t === 'no_action' || !p) {
    return (
      <Typography variant="body2" color="text.secondary">
        <em>
          No actionable pattern identified — Approve to acknowledge, or Reject.
        </em>
      </Typography>
    );
  }
  if (t === 'regression_test') {
    return (
      <Box>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Filename: <code>{p.proposed_filename}</code>{' '}
          <Button
            size="small"
            onClick={() => navigator.clipboard.writeText(p.proposed_filename)}
          >
            Copy
          </Button>
        </Typography>
        <pre className="code-block">{JSON.stringify(p.proposed_json_content, null, 2)}</pre>
        <Button
          size="small"
          onClick={() => navigator.clipboard.writeText(
            JSON.stringify(p.proposed_json_content, null, 2),
          )}
        >
          Copy JSON
        </Button>
        <CopyAsClaudePromptButton proposal={proposal} onCopied={onCopiedClaudePrompt} />
      </Box>
    );
  }
  if (t === 'rules_yaml') {
    const yaml = `- id: ${p.rule_id}\n  kind: ${p.rule_kind}\n  match_type: ${p.rule_match_type}\n  reason: ${JSON.stringify(p.rule_reason)}\n  match_any:\n${(p.rule_match_any || []).map((term) => `    - ${JSON.stringify(term)}`).join('\n')}\n`;
    return (
      <Box>
        <pre className="code-block">{yaml}</pre>
        <Button size="small" onClick={() => navigator.clipboard.writeText(yaml)}>
          Copy YAML
        </Button>
        <CopyAsClaudePromptButton proposal={proposal} onCopied={onCopiedClaudePrompt} />
      </Box>
    );
  }
  if (t === 'prompt_diff') {
    return (
      <Box>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Target: <code>{p.target_file}</code> §<code>{p.target_section}</code>
        </Typography>
        <pre className="code-block diff">{p.unified_diff_text}</pre>
        <Button
          size="small"
          onClick={() => navigator.clipboard.writeText(p.unified_diff_text)}
        >
          Copy diff
        </Button>
        <CopyAsClaudePromptButton proposal={proposal} onCopied={onCopiedClaudePrompt} />
      </Box>
    );
  }
  return null;
};

const ApplyInstructions = ({ proposal }) => {
  const t = proposal.proposal_type;
  const p = proposal.proposal_payload;
  if (t === 'regression_test') {
    return (
      <Typography variant="body2" component="div">
        <ol>
          <li>Save JSON to <code>tests/ivr_scripts/{p?.proposed_filename}</code></li>
          <li>Run <code>python3 tests/run_tests.py --script {p?.proposed_filename?.replace(/\.json$/, '')}</code></li>
          <li>Run <code>python3 tests/run_tests.py --core</code> to confirm no regressions</li>
          <li>Commit. The voice-agent deploy is unaffected — test scripts don&rsquo;t ship with the agent.</li>
        </ol>
      </Typography>
    );
  }
  if (t === 'rules_yaml') {
    return (
      <Typography variant="body2" component="div">
        <ol>
          <li>Insert the rule into the appropriate section of <code>Maia-CloudFunctions/voiceAgent/rules.yaml</code></li>
          <li>Run <code>python3 tests/run_tests.py --core</code> — must pass 100%</li>
          <li>Commit and re-deploy the voice agent (<code>Maia-CloudFunctions/voiceAgent/deploy_voice_agent.sh</code>)</li>
        </ol>
      </Typography>
    );
  }
  if (t === 'prompt_diff') {
    return (
      <Typography variant="body2" component="div">
        <ol>
          <li>Apply the unified diff to <code>{p?.target_file}</code></li>
          <li>Run <code>python3 tests/run_tests.py --core</code> — <strong>must pass 100%</strong></li>
          <li>Commit and re-deploy the voice agent</li>
        </ol>
      </Typography>
    );
  }
  return (
    <Typography variant="body2" color="text.secondary">
      No patch to apply for no_action proposals.
    </Typography>
  );
};

export default AdminImprovementRequests;
